const REAL_NAME_PATTERN = /^\p{L}[\p{L}\p{M}\s.'-]*$/u
const DIGITS_ONLY_PATTERN = /^\d+$/

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (typeof value === 'boolean') return value === false
  if (value instanceof Date) return Number.isNaN(value.getTime())
  return false
}

const isQuestionAnswered = (question, values) => {
  const value = values?.[question.key]

  if (question.key === 'agree_text') {
    return value === true
  }

  if (question.key === 'rules_link') {
    return value === 'clicked'
  }

  return !isEmptyValue(value)
}

const toStartCase = (value) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

const getQuestionLabel = (question) => {
  if (question?.label?.trim()) return question.label
  return toStartCase(question.key)
}

const validateApplication = (applicationQuestions, values) => {
  const errors = {}

  if (!applicationQuestions?.length) return errors

  for (const question of applicationQuestions) {
    const value = values?.[question.key]
    const label = getQuestionLabel(question)

    if (question.required && !isQuestionAnswered(question, values)) {
      errors[question.key] = `${label} is required.`
      continue
    }

    if (isEmptyValue(value)) continue

    if (question.key === 'real_name') {
      const realName = `${value}`.trim()
      if (realName && !REAL_NAME_PATTERN.test(realName)) {
        errors[question.key] =
          'Real name can only contain letters and punctuation.'
      }
    }

    if (question.key === 'driver_number') {
      const driverNumber = `${value}`.trim()
      if (driverNumber && !DIGITS_ONLY_PATTERN.test(driverNumber)) {
        errors[question.key] = 'Driver number must be numeric.'
      }
    }

    if (question.key === 'iracing_id') {
      const iracingId = `${value}`.trim()
      if (iracingId && !DIGITS_ONLY_PATTERN.test(iracingId)) {
        errors[question.key] = 'iRacing ID must be numeric.'
      }
    }
  }

  return errors
}

const normalizeFormValues = (values) => {
  const cleaned = {}

  Object.entries(values || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed.length === 0) return
      cleaned[key] = trimmed
      return
    }

    cleaned[key] = value
  })

  return cleaned
}

const validateUserApplication = (applicationQuestions, userApplication) => {
  const normalized = normalizeFormValues(userApplication)
  const questionKeys = new Set(
    (applicationQuestions || []).map((question) => question.key),
  )
  const normalizedKnown = Object.fromEntries(
    Object.entries(normalized).filter(([key]) => questionKeys.has(key)),
  )

  const hasAnyAnswer = Object.values(normalizedKnown).some((value) => {
    if (typeof value === 'boolean') return value === true
    return value !== null && value !== undefined
  })

  if (!hasAnyAnswer) {
    return {
      valid: false,
      message:
        'Please fill out at least one question to submit your application.',
    }
  }

  for (const question of applicationQuestions) {
    const value = normalizedKnown?.[question.key]
    const label = getQuestionLabel(question) || question.key

    if (question.required) {
      if (question.key === 'agree_text' && value !== true) {
        return { valid: false, message: 'Agreement is required.' }
      }

      if (question.key === 'rules_link' && value !== 'clicked') {
        return { valid: false, message: 'Please read and accept the rules.' }
      }

      if (value === null || value === undefined || value === '') {
        return {
          valid: false,
          message: `Missing required field: ${label}.`,
        }
      }
    }

    if (value === null || value === undefined || value === '') continue

    if (question.key === 'real_name') {
      const realName = `${value}`.trim()
      if (realName && !REAL_NAME_PATTERN.test(realName)) {
        return {
          valid: false,
          message: 'Real name can only contain letters and punctuation.',
        }
      }
    }

    if (question.key === 'driver_number') {
      const driverNumber = `${value}`.trim()
      if (driverNumber && !DIGITS_ONLY_PATTERN.test(driverNumber)) {
        return {
          valid: false,
          message: 'Driver number must be numeric.',
        }
      }
    }

    if (question.key === 'iracing_id') {
      const iracingId = `${value}`.trim()
      if (iracingId && !DIGITS_ONLY_PATTERN.test(iracingId)) {
        return {
          valid: false,
          message: 'iRacing ID must be numeric.',
        }
      }
    }
  }

  return { valid: true, normalized: normalizedKnown }
}

export {
  DIGITS_ONLY_PATTERN,
  REAL_NAME_PATTERN,
  getQuestionLabel,
  isEmptyValue,
  isQuestionAnswered,
  normalizeFormValues,
  validateApplication,
  validateUserApplication,
}
